import {
  COLUMN_HOUR_BILLED,
  COLUMN_HOUR_DATE,
  COLUMN_HOUR_LABELS,
  COLUMN_HOUR_NOTE,
  TASK_COLUMNS,
} from 'constants/export';
import {
  COLUMN_BUDGET,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_NAME,
  COLUMN_TIMESTAMPS,
} from 'constants/filters';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import type { HourAnalyticsContext } from 'types/analytics';
import type { RawAudit, RawHourAuditChanges } from 'types/audits';
import type { AutoNoteStrategy, Hour, HourDraft } from 'types/hour';
import type { GeneratedNotesStyleMap, HoursDetailsFilter } from 'types/hours';
import type { LightProject, Project } from 'types/project';
import type { Merge } from 'types/utility';
import { actionCreatorFactory } from 'typescript-fsa';
import { getDaysInMonth } from 'utils/date-helper';

const actionCreator = actionCreatorFactory('timely/hours');
export const hoursXhrStatusModule = createXhrStatusModule('hours');

/** Replaces all hours in state with the passed hours */
export const fetchedHours = actionCreator<Array<Hour<LightProject | number>>>('FETCHED_HOURS');
/** Replaces all hours in state with the passed hours */
export const replaceHours = actionCreator<Array<Hour<LightProject | number>>>('REPLACE_HOURS');
/** Updates specific hours in state with the passed hours */
export const updateHours = actionCreator<Array<Hour<LightProject | number>>>('UPDATE_HOURS');
/** Adds hours to the state. Overwrites any duplicates. Also updates draft if there is a timer and a draft. */
export const injectHours = actionCreator<Array<Hour<LightProject | number>>>('INJECT_HOURS');
/** Adds a single hour to the state. Overwrites if already exists. Updates project entity too. */
export const addHour = actionCreator<Hour<Project | number>>('ADD_HOUR');
/** Updates a single hour in the state, does not update project entity. */
export const editHour =
  actionCreator<
    Merge<
      Hour<number>,
      { id: number | null; project: Project | LightProject | number | null; user: { id: number } }
    >
  >('EDIT_HOUR');

export const clearHours = actionCreator('CLEAR_HOURS');
export const deleteHour = actionCreator<number>('DELETE_HOUR');
export const deleteHours = actionCreator<Array<number>>('DELETE_HOURS');
/** Used to notify other parts of the app that an hour has been saved by the user */
export const savedHour = actionCreator('SAVED_HOUR');
export const toggleHourHistoryModal = actionCreator('TOGGLE_HOUR_HISTORY_MODAL');

/**
 * The pending hour save that triggered the "join project" confirmation dialog
 * (ALL_PROJECTS_LOGGING — logging to a project the user isn't a member of).
 * Holds just enough to resume the save once the user picks a join mode.
 */
export interface JoinProjectPrompt {
  partialDraft: Partial<HourDraft>;
  options: { clearDraft: boolean };
  projectId: number;
  projectName: string;
}
export const showJoinProjectPrompt = actionCreator<JoinProjectPrompt>('SHOW_JOIN_PROJECT_PROMPT');
export const hideJoinProjectPrompt = actionCreator('HIDE_JOIN_PROJECT_PROMPT');
export const clearHourDraft = actionCreator<{
  /**
   * If `true`, the `draftOriginal` state will not be cleared.
   * @default false
   */
  preserveOriginal: boolean;
} | void>('CLEAR_HOUR_DRAFT');
export const unlockHourDraft = actionCreator('UNLOCK_HOUR_DRAFT');
export const setHourDraft = actionCreator<HourDraft>('SET_HOUR_DRAFT');
export const updateHourDraft = actionCreator<Partial<HourDraft>>('UPDATE_HOUR_DRAFT');
export const removeEntryIdsOnHourDraft = actionCreator<{
  entry_ids: Array<number>;
}>('REMOVE_ENTRY_IDS_ON_HOUR_DRAFT');
export const conditionallyRemoveEntryIdsOnHourDraft = actionCreator<{
  entry_ids: Array<number>;
  ignoreForProjectId: number;
}>('CONDITIONALLY_REMOVE_ENTRY_IDS_ON_HOUR_DRAFT');
export const toggleEntryIdsOnHourDraft = actionCreator<{
  entry_ids: Array<number>;
}>('TOGGLE_ENTRY_IDS_ON_HOUR_DRAFT');
export const addEntryIdsOnHourDraft = actionCreator<{
  entry_ids: Array<number>;
}>('ADD_ENTRY_IDS_ON_HOUR_DRAFT');

export const setLoggedWeeklyTotal = actionCreator<number>('SET_LOGGED_WEEKLY_TOTAL');

export const fetchedHourHistory = actionCreator<Array<unknown>>('hourHistory/FETCHED');
export const clearHourHistory = actionCreator('hourHistory/CLEAR_HOUR_HISTORY');

export const batchCreateHourDraft = actionCreator<{
  day: string;
  context: HourAnalyticsContext;
}>('BATCH_CREATE_HOUR_DRAFT');
export const clearBatchHourDraft = actionCreator('CLEAR_BATCH_HOUR_DRAFT');
export const updateBatchHourDraft = actionCreator<Partial<HourDraft>>('UPDATE_BATCH_HOUR_DRAFT');

export const setAutoNoteStrategy = actionCreator<AutoNoteStrategy>('SET_AUTO_NOTE_STRATEGY');
export const setGeneratedNotes = actionCreator<GeneratedNotesStyleMap | null>(
  'SET_GENERATED_NOTES'
);

export type HoursDictionary = Record<number, Hour<number>>;

export interface DefaultEntities {
  hour: Record<number, Hour<number>>;
  project: Record<number, LightProject>;
}

interface HourHistory {
  entities: {
    [id: number]: RawAudit<RawHourAuditChanges>;
  };
  result: Array<number>;
}

export interface State {
  showHourHistoryModal: boolean;
  /** Pending save awaiting the "join project" confirmation dialog, or null. */
  joinProjectPrompt: JoinProjectPrompt | null;
  autoNoteStrategy: AutoNoteStrategy;
  generatedNotes: GeneratedNotesStyleMap | null;
  draft: HourDraft | undefined;
  draftOriginal: Hour<number> | undefined;
  batchDraft: HourDraft | undefined;
  entities: DefaultEntities;
  result: Array<number>;
  loggedWeeklyTotal: number | null;
  history: HourHistory;
  xhrStatus: XhrStatus;
}

export type UpdateDraftFromHourProps = Pick<
  Hour<number>,
  'timer_state' | 'timer_stopped_on' | 'timer_started_on' | 'timestamps' | 'updated_at'
>;

const daysInMonth = getDaysInMonth(new Date().toISOString());
export const HOUR_DETAILS_INITIAL_FILTER: HoursDetailsFilter = {
  defaultFilterCount: 0,
  activeFilterCount: 0,
  from: daysInMonth[0],
  to: daysInMonth[daysInMonth.length - 1],
  timespan: 'unrestricted',
  billedState: null,
  lockedState: null,
  userIds: [],
  labelIds: [],
  eventIds: [],
  teamIds: [],
  projectIds: [],
  hourStateIds: [],
  sortBy: {
    field: 'day',
    direction: 'desc',
  },
  columns: [
    COLUMN_LOGGED_HOURS,
    COLUMN_LOGGED_MONEY,
    COLUMN_ESTIMATED_HOURS,
    COLUMN_ESTIMATED_MONEY,
    COLUMN_HOUR_BILLED,
    COLUMN_HOUR_DATE,
    COLUMN_HOUR_LABELS,
    COLUMN_HOUR_NOTE,
    COLUMN_NAME,
    COLUMN_TIMESTAMPS,
    COLUMN_BUDGET,
  ],
  taskColumns: [...TASK_COLUMNS],
  // XHR pagination
  limit: 100,
  page: 1,
  hasMore: false,
  resource: '',
};

export interface HoursDetailsBatchSelection {
  eventIds: Array<number>;
  projectIds: Array<number>;
  labelIds: Array<number>;
  billedState: null | boolean;
}

export const HOUR_DETAILS_INITIAL_BATCH_SELECTION: HoursDetailsBatchSelection = {
  eventIds: [],
  projectIds: [],
  labelIds: [],
  billedState: null,
};

export const HOUR_COLUMNS_ONLY_VISIBLE_IN_REPORTS: ReadonlyArray<string> = [COLUMN_BUDGET];

export const OPTIMISTIC_HOUR_IDENTIFIER = 1.1;

// identify hours we want to update due to push notification to avoid triggering a loading state
export const SYNC_BACKGROUND_CHANGES_KEY = 'sync-background-changes-key';
export const BATCH_API_SYNC_LIMIT = 20;

export interface Changes {
  hours: { from: number; to: number };
  minutes: { from: string; to: string };
  project: { from: LightProject; to: LightProject };
  note: { from: string; to: string };
  day: { from: string; to: string };
  hour_rate: { from: number; to: number };
  billed_at: { from: string | null; to: string };
  locked_at: { from: string | null; to: string };
  billable: { from: boolean; to: boolean };
  state_id: { from: number | null; to: number | null };
}

export enum ChangeKeys {
  hours = 'hours',
  minutes = 'minutes',
  project = 'project',
  note = 'note',
  day = 'day',
  hour_rate = 'hour_rate',
  billed_at = 'billed_at',
  billable = 'billable',
  locked_at = 'locked_at',
  state_id = 'state_id',
}
